import {
  createSystem,
  defineConfig,
  defaultConfig,
  mergeConfigs,
} from "@chakra-ui/react";
import { buttonRecipe } from "./recipes/_button";
import { dialogRecipe } from "./recipes/_dialog";
import { badgeRecipe } from "./recipes/_badge";
import { textRecipe } from "./recipes/_text";
import { colors, semanticColors } from "./_color";
import {
  themeColorSemanticColors,
  themeColorSemanticShadows,
} from "./_themeColorSemanticTokens";
import {
  blackShellBackground,
  defaultShellBackground,
  whiteShellBackground,
} from "../_components/ui/Sidebar/sidebarTheme/backgrounds";
import { progressRecipe } from "./recipes/_progress";
import { selectSlotRecipe } from "./recipes/_select";
import { toastRecipe } from "./recipes/_toast";
import { tooltipRecipe } from "./recipes/_tooltip";

/** Chakra default scale: base (0) + sm → 2xl; thêm 3xl/4xl cho layout 2K / 4K. */
const customConfig = defineConfig({
  conditions: {
    themeBlack: "[data-theme-color=black] &",
    themeWhite: "[data-theme-color=white] &",
  },
  theme: {
    breakpoints: {
      "3xl": "2560px",
      "4xl": "3840px",
    },
    tokens: {
      colors,
      fonts: {
        beVietnamPro: { value: "'Be Vietnam Pro', sans-serif" },
        heading: {
          value: "'Unbounded', sans-serif",
        },
        unbounded: { value: "'Unbounded', sans-serif" },
        body: { value: "'Be Vietnam Pro', sans-serif" },
        syne: { value: "'Syne', sans-serif" },
        mono: { value: "'JetBrains Mono', monospace" },
        spaceGrotesk: { value: "'Space Grotesk', sans-serif" },
      },
      shadows: {
        "custom-1": {
          value:
            "rgba(43, 42, 42, 0.15) 0px 2px 5px 0px, rgba(43, 42, 42, 0.15) 0px 1px 1px 0px",
        },
        "shadow-sm": {
          value: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        },

        shadow: {
          value:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },

        "shadow-md": {
          value:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        },

        "shadow-lg": {
          value:
            "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        },

        "shadow-xl": {
          value:
            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        },

        "shadow-2xl": {
          value: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
        },

        "shadow-inner": {
          value: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
        },

        "shadow-none": {
          value: "0 0 #0000",
        },
        "getcssscan-26": {
          value:
            "rgba(50, 50, 93, 0.25) 0px 30px 60px -12px, rgba(0, 0, 0, 0.3) 0px 18px 36px -18px",
        },
        "getcssscan-76": {
          value: "rgba(0, 0, 0, 0.08) 0px 4px 12px",
        },
        cardsm: {
          value: "0px 2px 8px rgba(0, 0, 0, 0.15) ",
        },
        airbnb1: {
          value: "rgba(0, 0, 0, 0.18) 0px 2px 4px",
        },
        github: {
          value:
            "rgba(27, 31, 35, 0.04) 0px 1px 0px, rgba(255, 255, 255, 0.25) 0px 1px 0px inset",
        },
        modal: {
          value: "0 24px 48px rgba(0,0,0,0.16)",
        },
        header: {
          value: "0px 0px 10px 0px rgba(0, 0, 0, 0.2)",
        },
        "shadow-primary-sm": {
          value: "0 1px 2px 0 rgb(91 33 244 / 0.15)",
        },

        "shadow-primary": {
          value:
            "0 1px 3px 0 rgb(91 33 244 / 0.1), 0 1px 2px -1px rgb(91 33 244 / 0.2)",
        },

        "shadow-primary-md": {
          value:
            "0 4px 6px -1px rgb(91 33 244 / 0.1), 0 2px 4px -2px rgb(91 33 244 / 0.25)",
        },

        "shadow-primary-lg": {
          value:
            "0 10px 15px -3px rgb(91 33 244 / 0.25), 0 4px 6px -4px rgb(91 33 244 / 0.25)",
        },

        "shadow-primary-xl": {
          value:
            "0 -6px 25px -5px rgb(91 33 244 / 0.5), 0 8px 16px -6px rgb(91 33 244 / 0.5)",
        },

        "shadow-primary-2xl": {
          value:
            "0 20px 25px -5px rgb(91 33 244 / 0.25), 0 16px 36px 6px rgb(91 33 244 / 0.25)",
        },
      },
    },
    semanticTokens: {
      colors: {
        ...semanticColors.colors,
        ...themeColorSemanticColors,
      },
      shadows: themeColorSemanticShadows,
    },
    recipes: {
      button: buttonRecipe,
      badge: badgeRecipe,
      text: textRecipe,
    },
    slotRecipes: {
      select: selectSlotRecipe,
      dialog: dialogRecipe,
      progress: progressRecipe,
      toast: toastRecipe,
      tooltip: tooltipRecipe,
    },
  },
  globalCss: {
    "#outer-container[data-theme-color='default']": defaultShellBackground,
    "#outer-container[data-theme-color='black']": blackShellBackground,
    "#outer-container[data-theme-color='white']": whiteShellBackground,
    a: {
      "&:hover": {
        borderBottom: "1px solid #333",
      },
    },
    "&::selection": {
      background: "{colors.primary.500}",
      color: "{colors.primary.50}",
    },
  },
  cssVarsPrefix: "tlf", // Talentify prefix
});

const config = mergeConfigs(defaultConfig, customConfig);
const system = createSystem(config);
export default system;
